import React, { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Button, Space, Card, Select, message, Spin, Row, Col, Typography, Alert, Switch } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import { useConfig } from '../../contexts/ConfigContext';
import { useCompany } from '../../contexts/CompanyContext';
import useAuth from '../../hooks/useAuth';
import { useSecureRequest } from '../../hooks/useSecureRequest';
import { useProductionCycle, useTraceableGroups } from '../../hooks/useProductionCycles';
import { useItems } from '../../hooks/useInventory';
import { useUser } from '../../contexts/UserContext';
import { useRegister } from '../../contexts/RegisterContext';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { ItemSearchSelect } from './ItemSearchSelect';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const CycleForm: React.FC = () => {
  const [form] = Form.useForm();
  const history = useHistory();
  const { uid } = useParams<{ uid?: string }>();
  const { config } = useConfig();
  const { company } = useCompany();
  const { user } = useUser();
  const { register } = useRegister();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const { securePost } = useSecureRequest();
  const [loading, setLoading] = useState(false);

  const umOptions = register.umOptions || [];

  const { data: cycleData, isLoading: isCycleLoading, error: cycleError } = useProductionCycle(tokenData, { cycle_uid: uid || undefined, company_uid: company.current?.uid });
  const { data: inventoryData, isLoading: isInventoryLoading } = useItems(tokenData, { company: company.current?.uid, module: config.module });
  const { groups: traceableGroups } = useTraceableGroups(tokenData, { company: company.current?.id });

  const isEditMode = !!uid;
  const inventoryItems = inventoryData?.itemList || [];
  const [dynamicItems, setDynamicItems] = useState<any[]>([]);
  const allItems = [...inventoryItems, ...dynamicItems];

  const status = Form.useWatch('status', form);
  const hasStages = Form.useWatch('has_stages', form);
  const initialElementId = Form.useWatch('initial_element_id', form);

  useEffect(() => {
    if (initialElementId && allItems?.length > 0) {
      const selectedItem = allItems.find((item: any) => item.id === initialElementId);
      if (selectedItem?.measure_unit) {
        form.setFieldValue('unit_of_measure', selectedItem.measure_unit);
      }
    }
  }, [initialElementId, allItems, form]);

  useEffect(() => {
    if (cycleError) {
      message.error(cycleError.message || "Error al cargar el ciclo productivo");
      history.push('/cycles');
    }
    if (isEditMode && !isCycleLoading && cycleData && !cycleData.cycle) {
      message.error(cycleData.message || "Ciclo productivo no encontrado");
      history.push('/cycles');
    }
  }, [cycleError, isCycleLoading, cycleData, isEditMode, history]);

  useEffect(() => {
    if (isEditMode && cycleData?.cycle) {
      const cycle = cycleData.cycle;
      form.setFieldsValue({
        name: cycle.name,
        description: cycle.description,
        cycle_category: cycle.cycle_category,
        productive_unit_type: cycle.productive_unit_type,
        initial_element_id: cycle.initial_element_id,
        initial_quantity: cycle.initial_quantity,
        unit_of_measure: cycle.unit_of_measure,
        start_date: cycle.start_date ? cycle.start_date.split('T')[0] : undefined,
        status: cycle.status,
        has_stages: cycle.has_stages,
        stages: cycle.production_cycle_stages || [],
        notes: cycle.notes
      });

      // Fetch linked group if exists
      if (tokenData?.key && cycle.id) {
        securePost('/traceability/get-cycle-group', { cycle_id: cycle.id, csrfToken: tokenData?.token?.uid }).then((res: any) => {
          if (res?.links && res.links.length > 0) {
            form.setFieldValue('traceable_group_id', res.links[0].group_id);
          }
        });
      }
    } else if (!isEditMode) {
      form.setFieldsValue({
        status: 'ACTIVE',
        cycle_category: 'FINITE_TRANSFORMATION',
        productive_unit_type: 'ANIMAL',
        has_stages: false,
        start_date: new Date().toISOString().split('T')[0]
      });
    }
  }, [isEditMode, cycleData, tokenData, form]);

  const onFinish = async (values: any) => {
    setLoading(true);

    const { traceable_group_id, ...cycleFields } = values;

    const payload = {
      ...cycleFields,
      company_id: company.current?.id,
      created_by_user_id: user?.id,
      start_date: values.start_date ? new Date(values.start_date + 'T12:00:00').toISOString() : new Date().toISOString(),
    };

    try {
      let savedCycleId = null;
      if (isEditMode) {
        payload.id = cycleData?.cycle.id;
        const res = await securePost('/manufacturing/update-cycle', {
          company_uid: company.current?.uid,
          cycle: payload,
          csrfToken: tokenData?.token?.uid
        });
        if (res?.cycle) {
          savedCycleId = res.cycle.id;
          message.success('Ciclo productivo actualizado correctamente');
        } else {
          message.error(res?.message || 'Error desconocido al actualizar');
        }
      } else {
        const res = await securePost('/manufacturing/create-cycle', {
          company_uid: company.current?.uid,
          cycle: payload,
          csrfToken: tokenData?.token?.uid
        });
        if (res?.cycle) {
          savedCycleId = res.cycle.id;
          message.success('Ciclo productivo creado correctamente');
        } else {
          message.error(res?.message || 'Error desconocido al crear');
        }
      }

      // Link traceable group if selected
      if (savedCycleId && traceable_group_id) {
        await securePost('/traceability/link-cycle-group', {
          cycle_id: savedCycleId,
          group_id: traceable_group_id,
          csrfToken: tokenData?.token?.uid
        });
      }

      if (savedCycleId) {
        history.push('/cycles');
      }
    } catch (err: any) {
      message.error(err.message || 'Error en la operación');
    }

    setLoading(false);
  };

  if (isEditMode && isCycleLoading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>;
  }

  // Guard por si el render ocurre antes de que el useEffect redirija (ciclo no encontrado en edit mode)
  if (isEditMode && !isCycleLoading && !cycleData?.cycle) return null;

  return (
    <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Title level={3}>{isEditMode ? `Editar Ciclo: ${cycleData?.cycle?.name}` : 'Nuevo Ciclo Productivo'}</Title>
        <Button onClick={() => history.push('/cycles')}>Volver</Button>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ status: 'ACTIVE', cycle_category: 'FINITE_TRANSFORMATION', productive_unit_type: 'ANIMAL', has_stages: false }}
      >
        <Card title="Información General" style={{ marginBottom: '24px' }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="name" label="Nombre del Ciclo (identificador)" rules={[{ required: true, message: 'Requerido' }]}>
                <Input placeholder="Ej. Lote Engorde 01-2026, Siembra Maíz Lote A" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="cycle_category" label="Categoría de Ciclo" rules={[{ required: true }]}>
                <Select>
                  <Option value="RECURRING_PRODUCTION">Producción Recurrente (Café, Cacao, Vaca lechera, etc.)</Option>
                  <Option value="FINITE_TRANSFORMATION">Transformación Finita (Ceba, Cultivo única cosecha, etc.)</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="productive_unit_type" label="Tipo de Unidad / Proceso" rules={[{ required: true }]}>
                <Select>
                  <Option value="BATCH">🏭 Lote / Batch de Fabricación</Option>
                  <Option value="HERD">🐄 Hato / Lote / Rebaño (Grupal)</Option>
                  <Option value="ANIMAL">🏷️ Animal individual</Option>
                  <Option value="CROP">🌱 Agrícola (Cultivo / Siembra)</Option>
                  <Option value="EQUIPMENT">⚙️ Equipo / Línea de Proceso</Option>
                  <Option value="CUSTOM">🧩 Personalizado / Otro</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="start_date" label="Fecha de Inicio" rules={[{ required: true }]}>
                <Input type="date" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="traceable_group_id"
                label="Grupo Trazable Asociado (Opcional)"
                help="Asocia este ciclo a un grupo o lote de trazabilidad individual para seguimiento de miembros, medidas y registros"
              >
                <Select
                  allowClear
                  showSearch
                  placeholder="Seleccionar grupo o lote registrado..."
                  optionFilterProp="children"
                >
                  {traceableGroups.map((g: any) => (
                    <Option key={g.id} value={g.id}>
                      {g.name} ({g.active_members_count || 0} miembros) — {g.module_options?.string_value || 'General'}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="description" label="Descripción (Opcional)">
                <TextArea rows={2} placeholder="Detalles de la orden, lote, especificaciones técnicas o genéticas..." />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Inicialización del Ciclo (Inventario de Partida)" style={{ marginBottom: '24px' }}>
          {status !== 'ACTIVE' && isEditMode && (
             <Alert style={{marginBottom: 16}} type="warning" showIcon message="Nota: Editar las cantidades iniciales de un ciclo cerrado puede no afectar el inventario de la misma forma." />
          )}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="initial_element_id" label="Elemento Inicial (Opcional - ej: Ternero, Semilla)" help="Seleccione si el ciclo inicia con un producto del inventario">
                <ItemSearchSelect
                  placeholder="Buscar elemento"
                  initialItems={allItems}
                  onItemSelect={(item) => {
                    if (item) {
                      if (!dynamicItems.some(i => i.id === item.id)) {
                        setDynamicItems(prev => [...prev, item]);
                      }
                      form.setFieldValue('unit_of_measure', item.measure_unit);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Row gutter={16}>
                <Col span={!initialElementId ? 12 : 24}>
                  <Form.Item name="initial_quantity" label="Cantidad Inicial">
                    <InputNumber 
                      style={{ width: '100%' }} 
                      min={0} 
                      suffix={allItems.find((i:any) => i.id === initialElementId)?.measure_unit || undefined} 
                    />
                  </Form.Item>
                </Col>
                {!initialElementId ? (
                  <Col span={12}>
                    <Form.Item name="unit_of_measure" label="Unidad de Medida" rules={[{ required: true, message: 'Requerida' }]}>
                      <Select
                        placeholder="Elegir unidad"
                        showSearch
                        optionFilterProp="children"
                        allowClear
                      >
                        {umOptions.map((um: any) => (
                          <Option key={um.key} value={um.key}>
                            {um.string_value} ({um.key})
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                ) : (
                  <Form.Item name="unit_of_measure" hidden>
                    <Input />
                  </Form.Item>
                )}
              </Row>
            </Col>
          </Row>
        </Card>

        <Card title={
            <Space>
              Definir Etapas (Opcional)
              <Form.Item name="has_stages" valuePropName="checked" noStyle>
                <Switch />
              </Form.Item>
            </Space>
          } 
          style={{ marginBottom: '24px' }}>
          
          {hasStages ? (
            <Form.List name="stages">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <Row key={key} gutter={16} align="middle" style={{ marginBottom: 8, padding: '16px', background: '#f5f5f5', borderRadius: '4px' }}>
                      <Col span={7}>
                        <Form.Item {...restField} name={[name, 'name']} label="Nombre Etapa" rules={[{ required: true, message: 'Ingrese nombre' }]}>
                          <Input placeholder="Ej. Crecimiento" />
                        </Form.Item>
                      </Col>
                      <Col span={5}>
                        <Form.Item {...restField} name={[name, 'duration_days']} label="Duración (días)">
                          <InputNumber min={1} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={9}>
                        <Form.Item {...restField} name={[name, 'description']} label="Descripción">
                          <Input placeholder="..." />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)}>
                          Eliminar
                        </Button>
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Agregar Etapa
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          ) : (
            <Text type="secondary">Puede habilitar las etapas cronológicas (ej: Levante, Ceba) activando el switch de la sección.</Text>
          )}
        </Card>

        <Card title="Observaciones Adicionales" style={{ marginBottom: '24px' }}>
          <Form.Item name="notes" label="Notas">
            <TextArea rows={4} placeholder="Escriba aquí cualquier información importante sobre este ciclo productivo" />
          </Form.Item>
        </Card>

        <div style={{ position: 'sticky', bottom: '0', background: '#fff', padding: '16px', textAlign: 'right', boxShadow: '0 -2px 10px rgba(0,0,0,0.1)', zIndex: 10 }}>
          <Space>
            <Button onClick={() => history.push('/cycles')}>Cancelar</Button>
            <Button type="primary" htmlType="submit" loading={loading} disabled={status === 'CLOSED' || status === 'CANCELLED'}>
              {isEditMode ? 'Guardar Cambios' : 'Crear Ciclo'}
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  );
};

export default CycleForm;
